import { createSelector } from '@reduxjs/toolkit';

import { CURRENCY_SIGN } from '@/constants/countries';

import { FeatureName } from '@/types/app-configurations';

import { RootState } from '../store';

/**
 * Base config selectors with no cross-slice dependencies.
 * Used by user-data/selectors to avoid circular dependency.
 */
export const selectConfigsState = createSelector(
  (state: RootState) => state.configs,
  data => ({
    ...data,
  }),
);

export const selectDefaultCurrency = createSelector(
  selectConfigsState,
  ({ defaultCurrency }) => {
    const currency = defaultCurrency || '';
    const currencyDisplay = CURRENCY_SIGN[currency] || currency;
    return { currency, currencyDisplay };
  },
);

export const selectFeatureList = createSelector(
  selectConfigsState,
  data => data.features,
);

export const selectIsFeatureInitialized = createSelector(
  selectConfigsState,
  data => data.initialized.features,
);

export const selectIsFeatureEnabled = (feature: FeatureName) =>
  createSelector(selectConfigsState, data =>
    (data?.features ?? []).some(
      item => item.feature === feature && item.enabled,
    ),
  );

export const selectMailingAddressFeature = selectIsFeatureEnabled(
  FeatureName.MailingAddress,
);

export const selectRightStartFeature = selectIsFeatureEnabled(
  FeatureName.RightStartAccount,
);

export const selectRightStartComingSoonFeature = selectIsFeatureEnabled(
  FeatureName.RightStartComingSoon,
);

export const selectNewApplicationTypeSelectionFeature = selectIsFeatureEnabled(
  FeatureName.NewApplicationTypeSelection,
);

export const selectRemediationFeature = selectIsFeatureEnabled(
  FeatureName.Remediation,
);

export const selectYourFinancesFeature = selectIsFeatureEnabled(
  FeatureName.YourFinances,
);

export const selectAdobeSignFormFeature = selectIsFeatureEnabled(
  FeatureName.AdobeSignForm,
);

export const selectLivenessrecordDisabled = selectIsFeatureEnabled(
  FeatureName.DisableLivenessRecord,
);

export const selectLivenessSystem = createSelector(
  selectConfigsState,
  data =>
    data.features.find(item => item.feature === FeatureName.LivenessSystem)
      ?.value,
);
